import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES,
  AxisSubIndustryId,
} from '../axisNewClientConstants';
import {
  FormFooter,
  NewClientInquiryForm,
  PrimaryButton,
  SecondaryButton,
  StepActions,
} from './NewClientInquiryForm';
import {
  AxisSubmitAndExitFollowUpText,
  AXIS_CONTINUE_APPLICATION_FOLLOW_UP_MESSAGE,
} from '../components/AxisSubmitAndExitFollowUpText';
import { scrollToElement } from '../scrollToElement';

interface SpecializedNewClientFormsProps {
  subIndustryId: AxisSubIndustryId | null;
  onBack: () => void;
}

interface ContinueApplicationFormsProps extends SpecializedNewClientFormsProps {
  inquiryFormInternalName: string;
  applicationFormInternalName: string;
}

const ContinueApplicationForms = ({
  inquiryFormInternalName,
  applicationFormInternalName,
  subIndustryId,
  onBack,
}: ContinueApplicationFormsProps) => {
  const [showingApplication, setShowingApplication] = useState(false);
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  const applicationSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showingApplication) {
      return;
    }

    scrollToElement(applicationSectionRef.current);
  }, [showingApplication]);

  return (
    <FormsStack>
      <NewClientInquiryForm
        formInternalName={inquiryFormInternalName}
        isContractorFlow={false}
        subIndustryId={subIndustryId}
        submissionStarted={inquirySubmitting}
        setSubmissionStarted={setInquirySubmitting}
        onContinueApplication={() => setShowingApplication(true)}
        onBack={onBack}
        renderActions={
          showingApplication || inquirySubmitting
            ? () => null
            : ({ setSubmitAction }) => (
                <FormFooter>
                  <AxisSubmitAndExitFollowUpText
                    message={AXIS_CONTINUE_APPLICATION_FOLLOW_UP_MESSAGE}
                  />
                  <StepActions>
                    <SecondaryButton type='button' onClick={onBack}>
                      Back
                    </SecondaryButton>
                    <PrimaryButton
                      type='submit'
                      onClick={() => {
                        setSubmitAction('continueApplication');
                      }}
                    >
                      Continue to Application
                    </PrimaryButton>
                    <SecondaryButton
                      type='submit'
                      onClick={() => {
                        setSubmitAction('thanks');
                      }}
                    >
                      Submit Form
                    </SecondaryButton>
                  </StepActions>
                </FormFooter>
              )
        }
      />
      {showingApplication ? (
        <div ref={applicationSectionRef}>
          <NewClientInquiryForm
            formInternalName={applicationFormInternalName}
            isContractorFlow={false}
            subIndustryId={subIndustryId}
            submissionStarted={applicationSubmitting}
            setSubmissionStarted={setApplicationSubmitting}
            onBack={onBack}
          />
        </div>
      ) : null}
    </FormsStack>
  );
};

export const EngineersForms = (props: SpecializedNewClientFormsProps) => (
  <ContinueApplicationForms
    inquiryFormInternalName={AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.engineers}
    applicationFormInternalName={
      AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.engineersApplication
    }
    {...props}
  />
);

export const ArchitectsForms = (props: SpecializedNewClientFormsProps) => (
  <ContinueApplicationForms
    inquiryFormInternalName={AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.architects}
    applicationFormInternalName={
      AXIS_NEW_CLIENT_FORM_INTERNAL_NAMES.architectsApplication
    }
    {...props}
  />
);

const FormsStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));
